import { computed, reactive, shallowRef, watch } from 'vue'
import { tryOnBeforeUnmount } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import {
  isGcomeKeyTravelInfo,
  type GcomeKeyTravel,
  type GcomeTriggerType,
  type KeyTravelList,
} from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { useItinerarySDK } from './useItinerarySDK'

export const DEFAULT_GCOME_KEY_TRAVEL: GcomeKeyTravel = {
  axisType: 0,
  triggerType: 0, // 0 普通 / 1 RT 关连续 / 2 RT 开连续
  socdStrategy: 0x00,
  travel: 135,
  precisionBits: 0,
  pressRt: 50,
  pressDeadzone: 10,
  releaseRt: 50,
  releaseDeadzone: 10,
}

export type RtPrecisionIndex = 0 | 1 | 2 | 3

const RT_MIN_MM = 0.1
const RT_MAX_MM = 3.3

/** RT 精度档：步进 + 按下/释放精度位。同一档同时作用于 press/release。 */
export const RT_PRECISION_OPTIONS = [
  { step: 0.1, pressBits: 0x03, releaseBits: 0x0c, label: '0.100mm' },
  { step: 0.01, pressBits: 0x00, releaseBits: 0x00, label: '0.010mm' },
  { step: 0.005, pressBits: 0x01, releaseBits: 0x04, label: '0.005mm' },
  { step: 0.001, pressBits: 0x02, releaseBits: 0x08, label: '0.001mm' },
] as const

const DEFAULT_PRECISION_INDEX: RtPrecisionIndex = 1

const isRtEnabled = (triggerType: GcomeTriggerType) => triggerType !== 0
const isContinuousEnabled = (triggerType: GcomeTriggerType) => triggerType === 2

const precisionBitsOf = (index: RtPrecisionIndex) => {
  const option = RT_PRECISION_OPTIONS[index]
  return option.pressBits | option.releaseBits
}

const PRESS_PRECISION_MASK = 0x03

const precisionIndexOf = (precisionBits: number): RtPrecisionIndex => {
  const pressBits = precisionBits & PRESS_PRECISION_MASK
  const index = RT_PRECISION_OPTIONS.findIndex((item) => item.pressBits === pressBits)
  return (index >= 0 ? index : DEFAULT_PRECISION_INDEX) as RtPrecisionIndex
}

export const getRtPrecisionStep = (precisionBits: number) => {
  const pressBits = precisionBits & PRESS_PRECISION_MASK
  const option = RT_PRECISION_OPTIONS.find((item) => item.pressBits === pressBits)
  return option?.step ?? 0.01
}

export const getRtDecimalPlaces = (step: number) => (step >= 0.1 ? 1 : step >= 0.01 ? 2 : 3)

export const formatRtSensitivityMm = (steps: number, precisionBits: number) => {
  const step = getRtPrecisionStep(precisionBits)
  return (steps * step).toFixed(getRtDecimalPlaces(step))
}

const clampRtSteps = (steps: number, step: number) => {
  const min = Math.round(RT_MIN_MM / step)
  const max = Math.round(RT_MAX_MM / step)
  return Math.min(max, Math.max(min, Math.round(steps)))
}

/** 把毫米值对齐到新步进上更近的一档（距离相等时取较小值）。 */
const snapMmToNearestSteps = (mm: number, step: number) => {
  const scaled = mm / step
  const lower = Math.floor(scaled)
  const upper = Math.ceil(scaled)
  if (lower === upper) return lower
  const lowerDiff = Math.abs(mm - lower * step)
  const upperDiff = Math.abs(mm - upper * step)
  return lowerDiff <= upperDiff ? lower : upper
}

/** 精度切换时保留毫米值，再按新步进就近取整。 */
const rescaleRtSteps = (steps: number, fromStep: number, toStep: number) => {
  const mm = steps * fromStep
  return clampRtSteps(snapMmToNearestSteps(mm, toStep), toStep)
}

/** 行程页可编辑字段。合并到 keyTravelMap 时保留轴体类型 / SOCD。 */
const pickItineraryFields = (travel: GcomeKeyTravel): Partial<GcomeKeyTravel> => ({
  triggerType: travel.triggerType,
  travel: travel.travel,
  precisionBits: travel.precisionBits,
  pressRt: travel.pressRt,
  pressDeadzone: travel.pressDeadzone,
  releaseRt: travel.releaseRt,
  releaseDeadzone: travel.releaseDeadzone,
})

// 跨页面实例保留去抖任务并串行化写入，使新页面初始化能冲刷、等待旧页面的任务。
let itineraryCommitQueue: Promise<void> = Promise.resolve()
let itineraryCommitTimer: ReturnType<typeof setTimeout> | undefined
let pendingItineraryCommit: (() => Promise<void>) | undefined

const enqueueItineraryCommit = (task: () => Promise<void>) => {
  itineraryCommitQueue = itineraryCommitQueue.then(task, task)
  return itineraryCommitQueue
}

const scheduleItineraryCommit = (task: () => Promise<void>) => {
  pendingItineraryCommit = task
  if (itineraryCommitTimer) clearTimeout(itineraryCommitTimer)
  itineraryCommitTimer = setTimeout(() => {
    itineraryCommitTimer = undefined
    const pending = pendingItineraryCommit
    pendingItineraryCommit = undefined
    if (pending) void enqueueItineraryCommit(pending)
  }, 300)
}

const flushItineraryCommits = async () => {
  if (itineraryCommitTimer) {
    clearTimeout(itineraryCommitTimer)
    itineraryCommitTimer = undefined
  }

  const pending = pendingItineraryCommit
  pendingItineraryCommit = undefined
  if (pending) await enqueueItineraryCommit(pending)
  else await itineraryCommitQueue
}

/**
 * 触发行程 / RT 模式 / 连续快速触发 / 死区 / RT 灵敏度与精度的 UI 编辑状态。
 *
 * 回显锚点是当前选区的第一颗键：首次选中（或第一颗被换掉）时，把它当时的行程写入 editingState。
 * 之后加选不会改面板；取消全部选中时恢复初始默认。
 * 下一次拖动 slider / 拨开关，才把当前整份行程状态刷到所有选中键并写入设备。
 *
 * 灵敏度关联：回显第一颗键时按 pressRt === releaseRt 判定；
 * 调节过程中两值变相同也不会自动关联。
 */
export function useItinerarySetting() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const { getKeyItineraryList, setSelectedKeyItinerary } = useItinerarySDK()

  const selectedKeys = shallowRef<Array<{ index: number }>>([])
  const keyTravelMap = reactive(new Map<number, GcomeKeyTravel>())
  const editingState = reactive<GcomeKeyTravel>({ ...DEFAULT_GCOME_KEY_TRAVEL })
  const isInitializing = shallowRef(false)
  let loadToken = 0
  let pendingWriteIndexes: number[] = []
  const rtPrecisionIndex = shallowRef<RtPrecisionIndex>(DEFAULT_PRECISION_INDEX)
  const isRelated = shallowRef(
    DEFAULT_GCOME_KEY_TRAVEL.pressRt === DEFAULT_GCOME_KEY_TRAVEL.releaseRt,
  )

  const canEditTrigger = computed(() => selectedKeys.value.length > 0)
  const triggerSliderDisabled = computed(() => !canEditTrigger.value)
  const rtSwitchDisabled = computed(() => !canEditTrigger.value)
  const deadZoneEnabled = canEditTrigger
  const rtTriggerEnable = computed(() => isRtEnabled(editingState.triggerType))
  const continuousTriggerEnable = computed(() => isContinuousEnabled(editingState.triggerType))
  /** RT 灵敏度 / 精度：选中按键且 RT 模式打开后可用。 */
  const rtPrecisionEnabled = computed(() => canEditTrigger.value && rtTriggerEnable.value)
  const rtSensitivityEnabled = rtPrecisionEnabled
  const rtPrecisionStep = computed(() => RT_PRECISION_OPTIONS[rtPrecisionIndex.value].step)
  const rtPrecisionLabels = RT_PRECISION_OPTIONS.map((option) => option.label)

  const getKeyTravel = (index: number) => keyTravelMap.get(index) ?? { ...DEFAULT_GCOME_KEY_TRAVEL }

  const populateKeyTravelMap = (list: KeyTravelList | undefined) => {
    if (!list) return
    for (const item of list) {
      if (!isGcomeKeyTravelInfo(item)) continue
      keyTravelMap.set(item.index, { ...item.keyTravel })
    }
  }

  const commitTravelImmediate = async () => {
    const indexes = pendingWriteIndexes
    pendingWriteIndexes = []
    if (!indexes.length) return
    try {
      await setSelectedKeyItinerary(
        indexes.map((index) => ({
          index,
          keyTravel: getKeyTravel(index),
        })),
      )
    } catch {
      // useItinerarySDK 已记录并上报
    }
  }

  const commitTravel = () => {
    scheduleItineraryCommit(commitTravelImmediate)
  }

  /** 把当前面板行程字段刷到所有选中键并去抖写入设备，后加入的键也跟当前编辑值对齐。 */
  const applyEditingStateToSelected = () => {
    if (!selectedKeys.value.length) return
    const patch = pickItineraryFields(editingState)
    const indexes: number[] = []
    for (const { index } of selectedKeys.value) {
      const existing = keyTravelMap.get(index) ?? { ...DEFAULT_GCOME_KEY_TRAVEL }
      keyTravelMap.set(index, { ...existing, ...patch })
      indexes.push(index)
    }
    pendingWriteIndexes = [...new Set([...pendingWriteIndexes, ...indexes])]
    commitTravel()
  }

  const resetEditingStateToDefault = () => {
    Object.assign(editingState, DEFAULT_GCOME_KEY_TRAVEL)
    rtPrecisionIndex.value = DEFAULT_PRECISION_INDEX
    isRelated.value = DEFAULT_GCOME_KEY_TRAVEL.pressRt === DEFAULT_GCOME_KEY_TRAVEL.releaseRt
  }

  /** 把指定键当时的行程回显到面板（含精度档与灵敏度关联）。 */
  const loadEditingFromKey = (index: number) => {
    const travel = getKeyTravel(index)
    Object.assign(editingState, travel)
    rtPrecisionIndex.value = precisionIndexOf(travel.precisionBits)
    isRelated.value = travel.pressRt === travel.releaseRt
  }

  const loadFromDevice = async () => {
    const token = ++loadToken
    isInitializing.value = true
    try {
      await flushItineraryCommits()
      if (token !== loadToken) return
      const list = await getKeyItineraryList()
      if (token !== loadToken) return
      populateKeyTravelMap(list)
      const first = selectedKeys.value[0]
      if (first) loadEditingFromKey(first.index)
    } catch {
      // useItinerarySDK 已记录并上报
    } finally {
      if (token === loadToken) isInitializing.value = false
    }
  }

  watch(
    () => currentDevice.value,
    (device) => {
      if (device) void loadFromDevice()
    },
    { immediate: true },
  )

  tryOnBeforeUnmount(() => {
    void flushItineraryCommits()
  })

  /**
   * 选区第一颗键变化才回显；加选保持第一颗选中时的面板状态。
   * 取消全部选中时，右侧恢复初始默认。
   */
  const handleSelectionChange = (keys: Array<{ index: number }>) => {
    const prevFirst = selectedKeys.value[0]?.index
    selectedKeys.value = keys
    if (!keys.length) {
      resetEditingStateToDefault()
      return
    }
    const nextFirst = keys[0].index
    if (nextFirst !== prevFirst) loadEditingFromKey(nextFirst)
  }

  /**
   * 触发行程滑动条改变
   */
  const handleTravelChange = (value: number) => {
    editingState.travel = value
    applyEditingStateToSelected()
  }

  /**
   * RT模式更改
   */
  const handleRtTriggerEnableChange = (value: boolean) => {
    // 打开 RT → 1（连续保持关闭）；关闭 RT → 0（普通）
    editingState.triggerType = value ? 1 : 0
    applyEditingStateToSelected()
  }

  /**
   * 连续快速触发更改
   */
  const handleContinuousTriggerEnableChange = (value: boolean) => {
    if (!isRtEnabled(editingState.triggerType)) return
    editingState.triggerType = value ? 2 : 1
    applyEditingStateToSelected()
  }

  /**
   * 顶部死区（按下死区）更改
   */
  const handlePressDeadzoneChange = (value: number) => {
    editingState.pressDeadzone = value
    applyEditingStateToSelected()
  }

  /**
   * 底部死区（释放死区）更改
   */
  const handleReleaseDeadzoneChange = (value: number) => {
    editingState.releaseDeadzone = value
    applyEditingStateToSelected()
  }

  /**
   * 按下 RT 灵敏度更改。关联开启时同步释放灵敏度。
   */
  const handlePressRtChange = (value: number) => {
    if (!rtSensitivityEnabled.value) return
    editingState.pressRt = value
    if (isRelated.value) editingState.releaseRt = value
    applyEditingStateToSelected()
  }

  /**
   * 释放 RT 灵敏度更改。关联开启时同步按下灵敏度。
   */
  const handleReleaseRtChange = (value: number) => {
    if (!rtSensitivityEnabled.value) return
    editingState.releaseRt = value
    if (isRelated.value) editingState.pressRt = value
    applyEditingStateToSelected()
  }

  /**
   * 手动切换关联。不会因两值被调成相同而自动改写该状态。
   */
  const handleRelatedChange = (value: boolean) => {
    if (!rtSensitivityEnabled.value) return
    isRelated.value = value
  }

  /**
   * RT 精度更改：只改步进与小数位。毫米值尽量保持，无法整除新步进时就近对齐。
   */
  const handleRtPrecisionChange = (index: RtPrecisionIndex) => {
    if (!rtPrecisionEnabled.value) return
    const fromStep = rtPrecisionStep.value
    const toStep = RT_PRECISION_OPTIONS[index].step
    rtPrecisionIndex.value = index
    editingState.precisionBits = precisionBitsOf(index)
    editingState.pressRt = rescaleRtSteps(editingState.pressRt, fromStep, toStep)
    editingState.releaseRt = rescaleRtSteps(editingState.releaseRt, fromStep, toStep)
    if (isRelated.value) editingState.releaseRt = editingState.pressRt
    applyEditingStateToSelected()
  }

  return {
    // 行程数据状态
    keyTravelMap,
    editingState,
    isInitializing,

    // 控件可用状态
    canEditTrigger,
    triggerSliderDisabled,
    rtSwitchDisabled,
    deadZoneEnabled,
    rtTriggerEnable,
    continuousTriggerEnable,
    rtPrecisionEnabled,
    rtSensitivityEnabled,

    // RT 精度状态
    rtPrecisionIndex,
    rtPrecisionStep,
    rtPrecisionLabels,

    // RT 灵敏度关联状态
    isRelated,

    // 行程数据读取
    getKeyTravel,

    // 选中按键处理
    handleSelectionChange,

    // 触发行程与 RT 模式处理
    handleTravelChange,
    handleRtTriggerEnableChange,
    handleContinuousTriggerEnableChange,

    // 死区处理
    handleReleaseDeadzoneChange,
    handlePressDeadzoneChange,

    // RT 灵敏度与精度处理
    handlePressRtChange,
    handleReleaseRtChange,
    handleRelatedChange,
    handleRtPrecisionChange,
  }
}
